/* eslint-disable import/no-named-as-default-member */
import isNil from 'lodash/isNil';

import { captureException } from '@zenbusiness/application-commons-logging';

import * as ROUTES from '#client/constants/routes';
import ENV from '#client/public-env';

const IGNORE_REDIRECT_TO = ['/', '/dashboard', '/d/logout', '/d/login', '/d/callback'];

/**
 * Adjust unique URLs to be non-unique and thus easily aggregatable.
 * E.g. There have been 1337 visits to `/dashboard/$uuid/domains`.
 *
 * @param {string} pathname
 * @returns {string} pathname without any customer/business unique identifiers
 */
const standardizePathname = (pathname) => {
  // React router can leave off the leading slash, add it back for consistency in analytics.
  const pageViewed = pathname?.indexOf('/') !== 0 ? `/${pathname}` : pathname;

  // Remove identifiers from company urls for analytics
  // Replace any UUID found with a non-unique 'uuid'
  return pageViewed.replace(
    /[A-Za-z0-9]{8}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{12}/g,
    '$uuid'
  );
};

/**
 * Temporary method to create grant_application URL
 *
 * @param {Array<string>} requiredToEnforce
 * @param {object} params
 * @param {string} baseUrl
 * @returns {string} URL
 */
const checkRequired = (requiredToEnforce, params, baseUrl) => {
  requiredToEnforce.forEach((requiredField) => {
    if (!params[requiredField]) {
      throw new Error(
        `URL (${baseUrl}) can not be formed without ${requiredField}. ${JSON.stringify(params)}`
      );
    }
  });
  return true;
};

const TEST_BASE_URL = `https://${ENV.DEPLOY_ENV === 'production' ? 'www' : 'www.dev'}.zenbusiness.com`;

export const buildGrantAppUrl = (baseUrl) => (params = {}) => {
  let returnUrl = `${baseUrl}`;
  Object.entries({
    ...params
  }).forEach(([param, val]) => {
    if (typeof val !== 'undefined' && val !== null) {
      if (param === 'businessEntityUuid') {
        returnUrl += val;
      }
    }
  });
  return returnUrl;
};
/**
 * Factory for ZenReg URL
 *
 * @param {string} baseUrl
 * @param {object} opts
 * @returns {function(object, object): string} URL
 */
export const buildZenRegUrlBuilder = (baseUrl, opts = {}) => {
  const {
    alias: { idAlias = 'companyId' } = {},
    params: buildParams = {},
    required = ['businessEntityUuid']
  } = opts;

  return (params = {}, overrides = {}) => {
    const { required: overrideRequired = null } = overrides;

    if (checkRequired(overrideRequired || required, params, baseUrl)) {
      const searchParams = new URLSearchParams();
      Object.entries({
        ...buildParams,
        ...params
      }).forEach(([param, val]) => {
        if (typeof val !== 'undefined' && val !== null) {
          if (param === 'businessEntityUuid') {
            searchParams.append(idAlias, val);
          } else {
            searchParams.append(param, val);
          }
        }
      });
      searchParams.append('fromDashboard', true);
      return `${baseUrl}?${searchParams.toString()}`;
    }
    return baseUrl;
  };
};

/**
 * Factory for ZenDash URL
 *
 * @param {string} baseUrl
 * @param {object} opts
 * @returns {string} URL
 */
const buildZenDashUrlBuilder = (baseUrl, opts = {}) => {
  const { params: buildParams = {}, required = ['businessEntityUuid'] } = opts;

  return (params = {}, overrides = {}) => {
    const { required: overrideRequired = null } = overrides;

    if (checkRequired(overrideRequired || required, params, baseUrl)) {
      return Object.entries({
        ...buildParams,
        ...params
      }).reduce(
        (url, [param, val]) => (typeof val !== 'undefined' && val !== null
          ? url.replace(new RegExp(`:${param}`, 'g'), val)
          : url),
        baseUrl
      );
    }
    return baseUrl;
  };
};

/**
 * All builders default with required fields as ['businessEntityUuid'].
 */
const zenRegUrlBuilderByProduct = {
  accounting: buildZenRegUrlBuilder('/r/cpascreening-registration/init'),
  banking: buildZenRegUrlBuilder('/r/banking-registration/init'),
  banking_resolution: buildZenRegUrlBuilder('/r/bankingres-registration/init'),
  basic_email: buildZenRegUrlBuilder('/r/basicemail-registration/init'),
  beneficial_ownership_filing: buildZenRegUrlBuilder('/r/register-boi/init'),
  business_kit: buildZenRegUrlBuilder('/r/register-business-kit/init'),
  business_license_report: buildZenRegUrlBuilder('/r/register-prods/init', {
    params: {
      isBusLic: true
    }
  }),
  change_of_registered_agent: buildZenRegUrlBuilder('/r/register-cora/init', {
    required: []
  }),
  corp_amendment: buildZenRegUrlBuilder('/r/amendment/init', {
    alias: {
      idAlias: 'business_entity_uuid'
    }
  }),
  corp_annual_report: buildZenRegUrlBuilder('/r/annual-report/init', {
    alias: {
      idAlias: 'business_entity_uuid'
    },
    required: ['businessEntityUuid', 'jurisdiction', 'filing_type']
  }),
  corp_bylaws: buildZenRegUrlBuilder('/r/opagreement-registration/init'),
  corporate_docs: buildZenRegUrlBuilder('/r/register-cd/init'),
  corp_formation: buildZenRegUrlBuilder('/r/registration-new/init'),
  dba_entity: buildZenRegUrlBuilder('/r/register-dbaentity/init'),
  domain_name_privacy: buildZenRegUrlBuilder('/r/domainprivacy-registration/init'),
  domain_name_reg: buildZenRegUrlBuilder('/r/domainname-registration/init'),
  ein_creation: buildZenRegUrlBuilder('/r/register-prods/init'),
  entity_name_reservation: buildZenRegUrlBuilder('/r/register-name-reservation/init'),
  ettf_renewal: buildZenRegUrlBuilder('/r/ettf-renewal/init'),
  register_zbm_plaid: buildZenRegUrlBuilder('/r/register-zbm-plaid/init'),
  google_ads_credit: buildZenRegUrlBuilder('/r/google-ads-registration/init'),
  llc_amendment: buildZenRegUrlBuilder('/r/amendment/init', {
    alias: {
      idAlias: 'business_entity_uuid'
    }
  }),
  llc_annual_report: buildZenRegUrlBuilder('/r/annual-report/init', {
    alias: {
      idAlias: 'business_entity_uuid'
    },
    required: ['businessEntityUuid', 'jurisdiction', 'filing_type']
  }),
  llc_formation: buildZenRegUrlBuilder('/shop/llc'),
  logo_builder: buildZenRegUrlBuilder('/lb/entry/', {
    alias: {
      idAlias: 'be'
    }
  }),
  logo_kit: buildZenRegUrlBuilder('/r/logokit-registration/init'),
  ny_publication: buildZenRegUrlBuilder('/r/register-ny-publication/init'),
  operating_agreement: buildZenRegUrlBuilder('/r/opagreement-registration/init'),
  online_notary_service: () => 'https://www.notarize.com/',
  standard_good_standing: buildZenRegUrlBuilder('/r/gs-registration/init'),
  static_website: buildZenRegUrlBuilder('/r/register-smb-website/init'),
  tax_services_dashboard: buildZenDashUrlBuilder('/dashboard/:businessEntityUuid/money/tax-services'),
  website_tools_bundle: buildZenRegUrlBuilder('/r/onlinetools-registration/init'),
  wfg_initial_report: buildZenRegUrlBuilder('/r/register-wfgir/init'),
  worry_free_service: buildZenRegUrlBuilder('/r/register-wfg/init'),
  bank_of_america_offer: buildZenDashUrlBuilder(
    '/dashboard/:businessEntityUuid/banking/bank-of-america-offer'
  ),
  bank_of_america_offer_modal: buildZenDashUrlBuilder(
    '/dashboard/:businessEntityUuid?m=bankOfAmericaOffer'
  ),
  zb_money_pro: buildZenRegUrlBuilder('/r/register-zbmpro/init'),
  zb_money_essential: buildZenRegUrlBuilder('/r/register-zbm-plaid/init'),
  zb_tax_advice: buildZenRegUrlBuilder('/r/tax-services/init'),
  zb_tax_advice_biztax_prep: buildZenRegUrlBuilder('/r/tax-services/init'),
  zb_biztax_prep: buildZenRegUrlBuilder('/r/tax-services/init'),
  zb_money_dashboard: buildZenDashUrlBuilder('/dashboard/:businessEntityUuid/money'),
  grant_application: buildGrantAppUrl('https://www.tfaforms.com/4972620?buenid='),
  zb_checking_preferred_modal: buildZenDashUrlBuilder(
    '/dashboard/:businessEntityUuid?m=checkingPreferred'
  ),
  zb_banking_dashboard: buildZenDashUrlBuilder('/d/b/:businessEntityUuid/dashboard')
};

/**
 * @typedef {string} UUID
 */

/**
 * Generate a ZenWebsite Editor URL
 *
 * @param {UUID} websiteUuid
 * @returns {string} url
 */
export const generateZenEditorUrl = (websiteUuid) => {
  if (!websiteUuid) {
    return null;
  }

  return `https://${ENV.DEPLOY_ENV === 'production' ? '' : 'dev.'
  }zenbusiness.com/w/website/${websiteUuid}`;
};

/**
 * @typedef {string} UUID
 */

/**
 * Generate website URL
 *
 * @param {string} websiteId
 * @returns {string} url
 */
export const generateZenSiteViewUrl = (websiteId) => {
  if (!websiteId) {
    return null;
  }
  return `https://${ENV.DEPLOY_ENV === 'production' ? 'sites' : 'sites.dev'
  }.zenbusiness.com/${websiteId}`;
};

/**
 * Generate a Fully Qualified URL
 *
 * @param {string} route
 * @returns {string} url
 */
export const generateZenUrl = (route) => {
  if (!route) {
    return null;
  }
  if (ENV.DEPLOY_ENV === 'webpack' || ENV.DEPLOY_ENV === 'local') {
    return `http://local.zenbusiness.com:8080/${route.replace(
      /^\/+/,
      ''
    )}`;
  }
  return `https://www${ENV.DEPLOY_ENV === 'production' ? '.' : '.dev.'}zenbusiness.com/${route.replace(
    /^\/+/,
    ''
  )}`;
};

/**
 * Safely get the pathname of a URL
 *
 * @param {string} rawUrl - Relative URL
 * @returns {boolean} safe path name
 */
export const cleanRedirectTo = (rawUrl) => {
  // Handles: null and undefined
  if (isNil(rawUrl)) {
    return ROUTES.dashboard;
  }

  // Handles: non-string variables
  if (typeof rawUrl !== 'string') {
    captureException(new Error(`"rawUrl" expected type string; recieved type ${typeof rawUrl}`), {
      tags: {
        errorInfo: {
          rawUrl,
          type: typeof rawUrl
        },
        frontend: ENV
      }
    });
    return ROUTES.dashboard;
  }

  try {
    let url = rawUrl.trim();
    // Remove trailing ?
    if (url.charAt(url.length - 1) === '?') {
      url = url.slice(0, -1);
    }
    // Remove environment origin if present
    if (url.startsWith(TEST_BASE_URL)) {
      url = url.slice(TEST_BASE_URL.length);
    }

    if (url.startsWith('//')) {
      throw new Error(`Protocol relative URLs are not allowed: ${url}`);
    }

    const destination = new URL(url, TEST_BASE_URL);

    if (destination.protocol !== 'https:') {
      throw new Error(`Unexpected protocol on redirect: ${destination.protocol}`);
    }

    if (destination.origin !== TEST_BASE_URL) {
      throw new Error(`Unexpected origin on redirect: ${destination.origin}`);
    }

    // Check for reserved characters
    // Reserved Characters: https://datatracker.ietf.org/doc/html/rfc3986#section-2.2
    // Unreserved Characters: https://datatracker.ietf.org/doc/html/rfc3986#section-2.3
    if (!url.startsWith(destination.pathname)) {
      throw new Error(`Reserved characters found in url: ${url}`);
    }

    if (IGNORE_REDIRECT_TO.includes(destination.pathname)) {
      return ROUTES.dashboard;
    }

    // NOTE: `TEST_BASE_URL` will always be present in the string
    // Return absolute pathname, search and hash /d/some/page?search#hash
    return destination.toString().slice(TEST_BASE_URL.length);
  } catch (err) {
    captureException(err, {
      tags: {
        errorInfo: { rawUrl },
        frontend: ENV.NAME
      }
    });
    return ROUTES.dashboard;
  }
};

/**
 * Generate a redirect path to shopping-ui
 * using the lead details to restore the flow state (if available)
 *
 * @param {object} leadDetails - Lead details object
 * @param {string} [leadDetails.flowName] - The flow name to redirect to
 * @param {string} [leadDetails.uuid] - The UUID to restore flow state
 * @returns {string} redirect URL - Falls back to '/shop/' if no lead details provided
 */
export const generateShoppingUiRedirectUrl = (leadDetails = {}) => {
  const { flowName, uuid } = leadDetails;
  const flowPath = flowName || '';
  const flowStateIdentifier = uuid && typeof uuid === 'string' ? `?restoreState=${uuid}` : '';
  return generateZenUrl(`/shop/${flowPath}${flowStateIdentifier}`);
};

/**
 * Build a 'zenreg' URL based on a product and parameters
 *
 * @param {string} product
 * @param {object} params
 * @param {object} overrides
 * @returns {string} zenreg url
 */
export const buildCartUrl = (product, params, overrides) => {
  const urlBuilder = zenRegUrlBuilderByProduct[product];
  if (!urlBuilder) {
    throw new Error(`URL for ${product} unsupported.`);
  }
  return urlBuilder(params, overrides);
};

export const getRedirectTo = () => {
  const url = new URL(window.location);
  return `${url.pathname}${url.search}`;
};

/**
 * Extract the current view from a pathname
 *
 * ex: getCurrentViewFromUrl("d/company/931173e0-1f11-4467-bfdc-3937da4a19fd") will return "company"
 *
 * @param {string} pathname - ex: "d/company/931173e0-1f11-4467-bfdc-3937da4a19fd"
 * @returns {string} the current view from the pathname
 */
export const getCurrentViewFromUrl = (pathname) => {
  const standardArr = standardizePathname(pathname)?.split('/');
  const withoutUuid = standardArr.filter((e) => e !== '$uuid');
  return withoutUuid.slice(-1)[0];
};
